import React from 'react';
import type { UploadedFile } from './FileUploader.types';
import {
    XMarkIcon,
    ArrowPathIcon,
    DocumentIcon,
    PhotoIcon,
    FilmIcon,
    MusicalNoteIcon
} from '@heroicons/react/24/outline'; // Start with Heroicons as they are likely available or use Lucide if preferred

interface FilePreviewProps {
    file: UploadedFile;
    onRemove: (id: string) => void;
    disabled?: boolean;
    dictionary?: {
        errorUploading?: string;
        retryTooltip?: string;
        uploadingCancel?: string;
        remove?: string;
    };
}

export const FilePreview: React.FC<FilePreviewProps> = ({
    file,
    onRemove,
    disabled = false,
    dictionary = {},
}) => {
    const isImage = file.type?.startsWith('image/');

    // Helper to get icon based on file type
    const getFileIcon = () => {
        if (isImage) return <PhotoIcon className="w-8 h-8 text-gray-400" />;
        if (file.type?.startsWith('video/')) return <FilmIcon className="w-8 h-8 text-gray-400" />;
        if (file.type?.startsWith('audio/')) return <MusicalNoteIcon className="w-8 h-8 text-gray-400" />;
        return <DocumentIcon className="w-8 h-8 text-gray-400" />;
    };

    return (
        <div className={`
            relative flex items-center p-3 rounded-lg border
            ${file.status === 'error' ? 'border-error-border bg-error-bg' : 'border-border-primary dark:border-dark-border-primary bg-bg-primary dark:bg-dark-bg-secondary'}
            transition-all duration-200
        `}>
            {/* Thumbnail / Icon */}
            <div className="flex-shrink-0 w-12 h-12 rounded-md overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center mr-4">
                {isImage && file.preview ? (
                    <img
                        src={file.preview}
                        alt={file.name}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    getFileIcon()
                )}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 mr-4">
                <div className="flex justify-between items-start mb-1">
                    <p className="text-sm font-medium text-content-primary dark:text-dark-content-primary truncate max-w-[80%]">
                        {file.name}
                    </p>
                    <span className="text-xs text-content-tertiary dark:text-dark-content-tertiary whitespace-nowrap ml-2">
                        {(file.size ? (file.size / 1024 / 1024).toFixed(2) : 0)} MB
                    </span>
                </div>

                {/* Status Bar / Error Message */}
                {file.status === 'error' ? (
                    <p className="text-xs text-error-content font-medium">
                        {file.error || dictionary.errorUploading || 'Error al subir'}
                    </p>
                ) : (
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 mt-2">
                        <div
                            className={`h-1.5 rounded-full transition-all duration-300 ${file.status === 'completed'
                                ? 'bg-green-500'
                                : 'bg-primary-500'
                                }`}
                            style={{ width: `${file.progress}%` }}
                        />
                    </div>
                )}
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-2">
                {/* Retry Button (only on error) */}
                {file.status === 'error' && !disabled && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            // Logic for retry could be handled by parent if we expose onRetry
                            // For now, removing is the default action
                            onRemove(file.id);
                        }}
                        className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 transition-colors"
                        title={dictionary.retryTooltip || "Reintentar (Eliminar y volver a agregar)"}
                    >
                        <ArrowPathIcon className="w-5 h-5" />
                    </button>
                )}

                {/* Cancel / Remove Button */}
                {!disabled && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onRemove(file.id);
                        }}
                        className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-red-500 transition-colors"
                        title={file.status === 'uploading' ? (dictionary.uploadingCancel || 'Cancelar subida') : (dictionary.remove || 'Eliminar')}
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                )}
            </div>
        </div>
    );
};
